import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  AsYouType,
  getExampleNumber,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

// PhoneInput — câmp telefon cu selector de prefix de țară (dropdown căutabil) + placeholder cu
// formatul exemplu al țării alese. Validarea reală se face cu libphonenumber-js. Valoarea emisă
// prin `onChange` e în format canonic E.164 (ex: +40712345678) când numărul e parsabil; altfel
// numărul brut (ca să eșueze validarea părintelui până e completat corect).
//
// Props: label, value, onChange(string), error, helper, required, disabled, defaultCountry.

const FLAG = (cc) =>
  cc.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

// Numele țărilor în română (cu fallback pe codul ISO dacă Intl nu e disponibil).
let regionNames;
try {
  regionNames = new Intl.DisplayNames(['ro'], { type: 'region' });
} catch (_) {
  regionNames = null;
}
const countryName = (cc) => {
  try {
    return regionNames?.of(cc) || cc;
  } catch (_) {
    return cc;
  }
};

// Lista de țări (cod ISO + prefix apel) — calculată o singură dată la încărcarea modulului.
const COUNTRY_OPTIONS = getCountries()
  .map((cc) => ({
    cc,
    code: getCountryCallingCode(cc),
    name: countryName(cc),
    flag: FLAG(cc),
  }))
  .sort((a, b) => a.name.localeCompare(b.name, 'ro'));

// Placeholder = numărul exemplu al țării, formatat național (ex pentru RO: 0712 345 678).
const examplePlaceholder = (cc) => {
  try {
    const ex = getExampleNumber(cc, examples);
    return ex ? ex.formatNational() : '';
  } catch (_) {
    return '';
  }
};

const PhoneInput = ({
  label,
  value,
  onChange,
  error,
  helper,
  required = false,
  disabled = false,
  defaultCountry = 'RO',
  onBlur,
}) => {
  const { t } = useTranslation();
  const [country, setCountry] = useState(defaultCountry);
  const [national, setNational] = useState('');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const lastEmitted = useRef(null);

  // Construiește valoarea de ieșire din (țară, număr național). E.164 dacă e parsabil.
  const compute = (cc, text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return '';
    const parsed = parsePhoneNumberFromString(trimmed, cc);
    if (parsed) return parsed.number; // E.164
    // Incomplet/invalid — întoarcem ceva reconstruibil care NU validează (părintele va arăta eroare).
    return `+${getCountryCallingCode(cc)}${trimmed.replace(/\D/g, '')}`;
  };

  const emit = (cc, text) => {
    const out = compute(cc, text);
    lastEmitted.current = out;
    onChange(out);
  };

  // Sincronizare cu valoarea externă (ex: încărcare în mod edit, reset). Reconstruim țara + numărul
  // național doar când valoarea diferă de ce am emis ultima dată (evită bucle de re-render).
  useEffect(() => {
    if (value === lastEmitted.current) return;
    if (!value) {
      setNational('');
      lastEmitted.current = value;
      return;
    }
    const parsed = parsePhoneNumberFromString(value, defaultCountry);
    if (parsed) {
      if (parsed.country) setCountry(parsed.country);
      setNational(parsed.formatNational());
    } else {
      setNational(value);
    }
    lastEmitted.current = value;
  }, [value, defaultCountry]);

  // Închidere dropdown la click în afară.
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNationalChange = (e) => {
    const raw = e.target.value;
    // Formatare „as you type" pentru afișare prietenoasă (ex: 0712 345 678).
    const formatted = new AsYouType(country).input(raw);
    setNational(formatted);
    emit(country, formatted);
  };

  const handleSelectCountry = (cc) => {
    setCountry(cc);
    setOpen(false);
    setSearch('');
    emit(cc, national);
  };

  const selected =
    COUNTRY_OPTIONS.find((o) => o.cc === country) ||
    COUNTRY_OPTIONS.find((o) => o.cc === defaultCountry);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.code.includes(q.replace('+', '')) ||
        o.cc.toLowerCase() === q
    );
  }, [search]);

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="input-label">
          {label}
          {required ? ' *' : ''}
        </label>
      )}
      <div className="flex gap-2">
        {/* Selector prefix țară */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className={`input flex items-center gap-1.5 shrink-0 w-[7.5rem] ${error ? 'input-error' : ''} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <span className="text-base leading-none">{selected?.flag}</span>
          <span className="text-sm">+{selected?.code}</span>
          <svg
            className={`w-4 h-4 ml-auto text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Număr național */}
        <input
          type="tel"
          className={`input flex-1 ${error ? 'input-error' : ''}`}
          value={national}
          onChange={handleNationalChange}
          onBlur={onBlur}
          placeholder={examplePlaceholder(country)}
          disabled={disabled}
        />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full sm:w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
          <div className="p-2 border-b border-slate-100 dark:border-slate-700">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('ui.phoneSearchPlaceholder')}
              className="input w-full text-sm"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 dark:text-slate-400 text-center">
                {t('ui.noCountryFound')}
              </div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.cc}
                  type="button"
                  onClick={() => handleSelectCountry(o.cc)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-primary-50 dark:hover:bg-primary-950/50 ${o.cc === country ? 'bg-primary-50 dark:bg-primary-950/50 font-medium' : 'text-slate-700 dark:text-slate-300'}`}
                >
                  <span className="text-base leading-none">{o.flag}</span>
                  <span className="flex-1 truncate">{o.name}</span>
                  <span className="text-slate-400 dark:text-slate-500">+{o.code}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="input-error-text" role="alert">
          {error}
        </p>
      )}
      {helper && !error && <p className="input-helper">{helper}</p>}
    </div>
  );
};

export default PhoneInput;
